/**
 * LoaTrackerWidget — Leave of Absence tracking (DRR-321)
 *
 * Tracks subject absences (hospital stays, family visits, etc.)
 * Important for billing, care scheduling, and bed management.
 * Uses workflow instances to track LOA-related workflows for the subject.
 */

import React, { useMemo } from 'react';
import {
  Stack,
  Text,
  Badge,
  Group,
  Card,
  Skeleton,
  Alert,
  Box,
  ScrollArea,
} from '@mantine/core';
import { IconDoorExit, IconHome, IconAlertCircle } from '@tabler/icons-react';
import { useGetInstancesBySubjectQuery, GqlInstanceStatus } from '@ferp/core/generated/graphql';
import type { WidgetProps } from '@ferp/core';

const LOA_WORKFLOW_PATTERNS = ['loa', 'leave', 'absence', 'hospital', 'visit', 'discharge'];

const STATUS_COLOR: Record<string, string> = {
  RUNNING: 'blue',
  COMPLETED: 'green',
  PAUSED: 'yellow',
  CANCELLED: 'gray',
  FAILED: 'red',
  PENDING: 'orange',
};

function isLoaWorkflow(workflowName: string): boolean {
  const lower = workflowName.toLowerCase();
  return LOA_WORKFLOW_PATTERNS.some(pattern => lower.includes(pattern));
}

export function LoaTrackerWidget({ subjectActorId }: WidgetProps) {
  const { data, loading, error } = useGetInstancesBySubjectQuery({
    variables: { subjectActorId: subjectActorId ?? '' },
    skip: !subjectActorId,
    fetchPolicy: 'cache-and-network',
  });

  const { loaInstances, activeCount } = useMemo(() => {
    const all = data?.instanceApiGetInstancesBySubject ?? [];
    const loa = all.filter(inst => isLoaWorkflow(inst.workflowName));
    const active = loa.filter(inst => inst.status === GqlInstanceStatus.RUNNING).length;
    return { loaInstances: loa, activeCount: active };
  }, [data]);

  if (loading && loaInstances.length === 0) {
    return (
      <Stack gap="sm">
        <Skeleton height={40} />
        <Skeleton height={60} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Error loading leave of absence data.
      </Alert>
    );
  }

  const hasActiveLoA = activeCount > 0;

  return (
    <Stack gap="sm">
      <Group gap="xs" justify="space-between">
        <Group gap="xs">
          <IconDoorExit size={16} color={hasActiveLoA ? 'var(--mantine-color-orange-6)' : 'gray'} />
          <Text fw={600} size="sm">Leave of Absence</Text>
        </Group>
        {hasActiveLoA ? (
          <Badge size="sm" color="orange">{activeCount} Active</Badge>
        ) : (
          <Badge size="sm" color="green">At Facility</Badge>
        )}
      </Group>

      {!hasActiveLoA && loaInstances.length === 0 && (
        <Alert icon={<IconHome size={16} />} color="green" variant="light">
          Subject is currently at facility. No leave of absence history.
        </Alert>
      )}

      {!hasActiveLoA && loaInstances.length > 0 && (
        <Alert icon={<IconHome size={16} />} color="green" variant="light">
          Subject is currently at facility.
        </Alert>
      )}

      {loaInstances.length > 0 && (
        <ScrollArea.Autosize mah={200}>
          <Stack gap="xs">
            {loaInstances.map((instance) => (
              <Card key={instance.id} withBorder padding="xs" radius="sm">
                <Group justify="space-between" wrap="nowrap">
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={500} size="sm" lineClamp={1}>
                      {instance.workflowName}
                    </Text>
                    <Group gap="xs" mt={4}>
                      <Badge size="xs" variant="outline" color="gray">
                        {instance.currentState}
                      </Badge>
                      {instance.instanceName && (
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {instance.instanceName}
                        </Text>
                      )}
                    </Group>
                  </Box>
                  <Badge size="xs" color={STATUS_COLOR[instance.status] ?? 'gray'}>
                    {instance.status}
                  </Badge>
                </Group>
              </Card>
            ))}
          </Stack>
        </ScrollArea.Autosize>
      )}

      {loaInstances.length === 0 && (
        <Text size="xs" c="dimmed">
          LOA tracking available via workflow instances.
        </Text>
      )}
    </Stack>
  );
}
